import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '../../hooks/useMediaQuery';
import { useConversation } from '../../contexts/ConversationContext';
import ConversationList from '../../features/ai-assistant/components/ConversationList';

function TherapistChatLayout({ children }) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const { currentSessionId, rehydrating, isLoading: contextLoading } = useConversation();
  
  // Track whether collapse was forced by guided mode (not user preference)
  const forcedByGuidedMode = useRef(false);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    // Default to collapsed when in note writing mode (guided workflows)
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('mode');
    if (mode === 'guided_pending' || mode === 'guided_today') {
      forcedByGuidedMode.current = true;
      return true;
    }
    const saved = localStorage.getItem('convSidebarCollapsed');
    return saved === 'true';
  });
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);

  // Only persist to localStorage when the user manually toggles, not when forced by guided mode
  useEffect(() => {
    if (!forcedByGuidedMode.current) {
      localStorage.setItem('convSidebarCollapsed', isCollapsed.toString());
    }
  }, [isCollapsed]);

  useEffect(() => {
    if (isMobile) {
      setMobileDrawerOpen(false);
    }
  }, [currentSessionId, location.pathname, isMobile]);

  const toggleCollapse = () => {
    // User is manually toggling, so clear the forced flag
    forcedByGuidedMode.current = false;
    setIsCollapsed(!isCollapsed);
  };

  const toggleMobileDrawer = () => {
    setMobileDrawerOpen(!mobileDrawerOpen);
  };

  // Debounce the loading indicator so fast loads don't flash skeletons.
  // Only show skeletons after 300ms of continuous loading.
  const rawLoading = rehydrating || contextLoading;
  const [showSkeleton, setShowSkeleton] = useState(false);
  const loadingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (rawLoading) {
      loadingTimer.current = setTimeout(() => setShowSkeleton(true), 300);
    } else {
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
        loadingTimer.current = null;
      }
      setShowSkeleton(false);
    }
    return () => {
      if (loadingTimer.current) {
        clearTimeout(loadingTimer.current);
      }
    };
  }, [rawLoading]);

  const isLoadingSidebar = showSkeleton;

  return (
    <div className="flex h-full w-full">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div
          className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
            isCollapsed ? 'w-16' : 'w-72'
          } border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 flex flex-col`}
        >
          {/* Collapse/Expand Button */}
          <div className="flex items-center justify-end p-2 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={toggleCollapse}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-5 w-5 transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>

          {/* Conversation List */}
          <div className="flex-1 overflow-y-auto">
            {isLoadingSidebar ? (
              <div className="mt-8 px-4">
                <div className="mb-6">
                  {!isCollapsed && (
                    <h3 className="text-sm font-medium uppercase text-gray-500 dark:text-gray-400 mb-2">
                      Conversations
                    </h3>
                  )}
                  <div className="space-y-2">
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        className="h-12 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse"
                      />
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <ConversationList isCollapsed={isCollapsed} />
            )}
          </div>
        </div>
      )}

      {/* Mobile Drawer Overlay */}
      {isMobile && mobileDrawerOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-[1400] overlay-root"
          onClick={toggleMobileDrawer}
          style={{ pointerEvents: 'auto' }}
        >
          <div
            className="fixed inset-y-0 left-0 w-72 bg-white dark:bg-gray-800 shadow-xl z-[1401] flex flex-col"
            onClick={(e) => e.stopPropagation()}
            data-interactive="true"
            style={{ pointerEvents: 'auto' }}
          >
            {/* Drawer Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold">Conversations</h2>
              <button
                onClick={toggleMobileDrawer}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Conversation List */}
            <div className="flex-1 overflow-y-auto">
              {isLoadingSidebar ? (
                <div className="mt-8 px-4">
                  <div className="mb-6">
                    <h3 className="text-sm font-medium uppercase text-gray-500 dark:text-gray-400 mb-2">
                      Conversations
                    </h3>
                    <div className="space-y-2">
                      {[1, 2, 3].map((i) => (
                        <div
                          key={i}
                          className="h-12 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse"
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <ConversationList isCollapsed={false} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col relative min-w-0 min-h-0">
        {/* Mobile Menu Button */}
        {isMobile && (
          <div className="absolute top-4 left-4 z-10">
            <button
              onClick={toggleMobileDrawer}
              className="p-2 rounded-lg bg-white dark:bg-gray-800 shadow-md hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-gray-700"
              title="Open conversations"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                />
              </svg>
            </button>
          </div>
        )}

        {/* Render TherapistPanel */}
        {children}
      </div>
    </div>
  );
}

export default TherapistChatLayout;
